import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/siteData';
import Breadcrumb from '@/components/base/Breadcrumb';
import { ArticleSchema, WebPageSchema } from '@/components/base/SchemaOrg';
import { siteConfig } from '@/mocks/siteData';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-24 pb-16 px-4 md:px-6 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500 text-sm mb-6">您访问的文章不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const pageTitle = `${article.title} - ${siteConfig.name}`;

  return (
    <>
      <WebPageSchema pageTitle={pageTitle} pageDescription={article.summary} />
      <ArticleSchema
        title={article.title}
        description={article.summary}
        image={article.image}
        author={article.author}
        datePublished={article.date}
      />

      <article className="pt-24 pb-16 md:pb-20 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
              { label: article.title },
            ]}
          />

          {/* Article Header */}
          <header className="mt-6 mb-10">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs rounded-full bg-accent-100 text-accent-700 font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              {article.summary}
            </p>
            <div className="flex items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200/60">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-xs font-semibold text-primary-600">
                    {article.author.charAt(0)}
                  </span>
                </div>
                <span className="font-medium text-foreground-800">{article.author}</span>
              </div>
              <span className="text-foreground-300">|</span>
              <span>{article.date}</span>
              <span className="text-foreground-300">|</span>
              <span>阅读时间 {article.readTime}</span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full object-cover object-top aspect-[21/9]"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Share Section */}
          <div className="mt-12 pt-8 border-t border-background-200/60">
            <div className="flex items-center gap-4">
              <span className="text-sm text-foreground-500">分享到：</span>
              <button className="w-10 h-10 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors">
                <i className="ri-wechat-line"></i>
              </button>
              <button className="w-10 h-10 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors">
                <i className="ri-weibo-line"></i>
              </button>
              <button className="w-10 h-10 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors">
                <i className="ri-link"></i>
              </button>
            </div>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/60">
              <h3 className="text-lg font-semibold text-foreground-900 font-heading mb-6">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group block rounded-lg border border-background-200/70 bg-background-50 overflow-hidden hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-semibold text-foreground-900 font-heading group-hover:text-primary-600 transition-colors line-clamp-2">
                        {related.title}
                      </h4>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-foreground-400">{related.date}</span>
                        <span className="text-xs text-foreground-400">{related.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}